import { Stack, TableBody, TableCell, TableRow } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { CheckIcon } from '@/shared/assets/icons/check-icon';
import { InfoIcon } from '@/shared/assets/icons/info-icon';
import { StyledFeatureOptionsTable } from '@/shared/components/ui/feature-options-table/feature-options-table.styled';
import {
  TFeatureOptionsTableProps,
  TFeatureOptionValue,
} from '@/shared/components/ui/feature-options-table/feature-options-table.types';
import { Tooltip } from '@/shared/components/ui/tooltip/tooltip';

export const FeatureOptionsTable: React.FC<TFeatureOptionsTableProps> = ({
  options,
  values,
}) => {
  const { t } = useTranslation();

  const getValue = (value: TFeatureOptionValue, type: string) => {
    if (type !== 'boolean' && !value) {
      return '';
    }
    return value;
  };

  const getTranslatedValueLabel = (label: TFeatureOptionValue) => {
    if (label === undefined || label === null || label === '') {
      return '';
    }
    const translated = t(`dynamic_form:${label}`);
    return translated.includes('dynamic_form:') ? label : translated;
  };

  const getOptionsList = () => {
    if (!options?.length) {
      return Object.keys(values ?? {}).map((key) => {
        const type = typeof values?.[key];
        return {
          label: t(`dynamic_form:${key}`),
          value: getValue(values?.[key], type),
          type,
          hint: '',
        };
      });
    }

    return options?.map((option) => {
      const type = typeof option?.defaultValue;
      const value = values?.[option.value];
      // An unset option runs on its default — show that, not a blank.
      const resolvedValue = value ?? option.defaultValue;
      const inputOptionValue = option.inputOptions.find(
        (opt) =>
          opt.value ===
          (type === 'boolean' ? Boolean(resolvedValue) : resolvedValue),
      )?.label;
      const _valueLabel = option.inputOptions?.length
        ? inputOptionValue
        : resolvedValue;

      return {
        label: t(`dynamic_form:${option.label}`),
        value: getTranslatedValueLabel(_valueLabel),
        type,
        hint: t(`dynamic_form:${option.description}`),
      };
    });
  };

  const renderValue = (value: TFeatureOptionValue, type: string) => {
    if (type === 'boolean' && typeof value === 'boolean' && value) {
      return <CheckIcon />;
    }
    return value;
  };

  return (
    <StyledFeatureOptionsTable>
      <TableBody>
        {getOptionsList()?.map(({ label, value, type, hint }) => (
          <TableRow key={label}>
            <TableCell>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap="1rem"
              >
                {label}
                {hint && (
                  <Tooltip title={hint}>
                    <InfoIcon color="textAdditional" />
                  </Tooltip>
                )}
              </Stack>
            </TableCell>
            <TableCell>{renderValue(value, type)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </StyledFeatureOptionsTable>
  );
};
